/**
 * Demo Flow Page — /demo (S3).
 *
 * Mirrors `BookingFlowPage`'s shell (same StepProgress, same BookingStepManager,
 * same header treatment) with demo copy and a two-step logical machine held in
 * local state — no `BookingStepProvider`: its validation is payment-shaped and
 * a demo has no payment. Visual steps stay three (Figma: Booking Time / Bay
 * Selection / Confirmation) via the shared `VisualStepProvider`.
 *
 * Logical steps: 0 = DemoTimeStep (authed members confirm directly from it);
 * 1 = DemoFinishStep (un-authed only — account creation + create).
 *
 * No nav links point here by design — admins deep-link it for campaigns
 * (`?club-id=&tenant-handle=` required, as everywhere).
 *
 * @module pages/demo/DemoFlowPage
 */

import { useMemo, useState } from 'react';
import { StepProgress, type Step } from '~/ui/navigation/StepProgress';
import { CalendarIcon, LocationIcon, CheckoutIcon } from '@butter/ui/icons';
import { VisualStepProvider, useVisualStep } from '~/services/internalApi/booking/context';
import { useClubQuery } from '~/services/internalApi/club/hooks/useClubQuery';
import { BookingStepManager } from '../booking/components/BookingStepManager';
import { TenantLink } from '~/lib/navigation';
import { DemoTimeStep } from './steps/DemoTimeStep';
import { DemoFinishStep } from './steps/DemoFinishStep';

const demoSteps: Step[] = [
  {
    id: 'booking-time',
    label: 'Booking Time',
    icon: <CalendarIcon className="size-5" />,
  },
  {
    id: 'bay-selection',
    label: 'Bay Selection',
    icon: <LocationIcon className="size-5" />,
  },
  {
    id: 'confirmation',
    label: 'Confirmation',
    icon: <CheckoutIcon className="size-5 text-sm" />,
  },
];

const pageTitles = [
  'Book your Demo',
  'Bay Selection',
  'Confirmation',
];

const pageSubtitles = [
  'Complete your demo to start booking',
  'Choose a demo bay',
  'Complete your details to confirm your demo',
];

function DemoFlowPageContent() {
  const { visualStep } = useVisualStep();
  // Two logical steps, plain state — see module docblock.
  const [currentStep, setCurrentStep] = useState<0 | 1>(0);

  const safeStepIndex = Math.max(0, Math.min(visualStep, pageTitles.length - 1));
  const pageTitle = useMemo(() => pageTitles[safeStepIndex], [safeStepIndex]);
  const pageSubtitle = useMemo(() => pageSubtitles[safeStepIndex], [safeStepIndex]);

  return (
    <>
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
          {pageTitle}
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          {pageSubtitle}
        </p>
      </div>

      <StepProgress steps={demoSteps} currentStep={visualStep} className="mt-16" />

      <BookingStepManager currentStep={currentStep}>
        <DemoTimeStep onAdvanceToFinish={() => setCurrentStep(1)} />
        <DemoFinishStep onBack={() => setCurrentStep(0)} />
      </BookingStepManager>
    </>
  );
}

export function DemoFlowPage() {
  // ClubGate (MainLayout) guarantees the club is loaded before pages render.
  const { data: club } = useClubQuery();

  // Demos-off: a full-page state, not an empty grid. The availability and
  // create endpoints refuse 409 behind this too — this is just the front door.
  if (club && !club.demoBookingsEnabled) {
    return (
      <div className="mx-auto max-w-lg py-16 text-center" data-testid="demo-not-offered">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
          Demo bookings aren&rsquo;t offered here
        </h1>
        <p className="mt-3 text-sm text-gray-600">
          This club doesn&rsquo;t currently offer demo sessions.
        </p>
        <TenantLink
          to="/"
          className="mt-8 inline-block rounded-full bg-theme-600 px-6 py-3 font-medium text-white transition-colors hover:bg-theme-700 focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2"
        >
          Make a Booking
        </TenantLink>
      </div>
    );
  }

  return (
    <VisualStepProvider>
      <DemoFlowPageContent />
    </VisualStepProvider>
  );
}
